/**
 * CustomerForm - T070
 *
 * Form for creating/editing customers
 */
import { useState, type FormEvent } from 'react';
import { GlassPanel, Input, Button, FormSaveBar, Modal } from '../ui';
import type { Customer } from '../../hooks/useCustomers';
import type { CreateCustomerInput } from '@armard/shared';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';

interface CustomerFormProps {
  customer?: Customer;
  onSubmit: (data: CreateCustomerInput & { version?: number }) => void;
  isLoading?: boolean;
  onCancel?: () => void;
}

export function CustomerForm({ customer, onSubmit, isLoading, onCancel }: CustomerFormProps) {
  const [formData, setFormData] = useState({
    companyName: customer?.companyName ?? '',
    contactFirstName: customer?.contactFirstName ?? '',
    contactLastName: customer?.contactLastName ?? '',
    email: customer?.email ?? '',
    phone: customer?.phone ?? '',
    defaultPaymentDays: customer?.defaultPaymentDays ?? 14,
    notes: customer?.notes ?? '',
  });
  const [editVersion] = useState(customer?.version);
  const draftGuard = useUnsavedChanges(formData);
  const [confirmCancel, setConfirmCancel] = useState(false);

  const requestCancel = () => {
    if (!onCancel) return;
    if (draftGuard.isDirty) setConfirmCancel(true);
    else onCancel();
  };

  const discardAndCancel = () => {
    draftGuard.markSaved();
    setConfirmCancel(false);
    onCancel?.();
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const optionalText = (value: string) => value || (customer ? null : undefined);
    onSubmit({
      companyName: formData.companyName,
      contactFirstName: optionalText(formData.contactFirstName),
      contactLastName: optionalText(formData.contactLastName),
      email: optionalText(formData.email),
      phone: optionalText(formData.phone),
      defaultPaymentDays: formData.defaultPaymentDays,
      notes: optionalText(formData.notes),
      ...(customer ? { version: editVersion } : {}),
    } as unknown as CreateCustomerInput & { version?: number });
  };

  return (
    <form onSubmit={handleSubmit}>
      <fieldset disabled={isLoading} className="border-0 p-0 m-0 min-w-0">
        <GlassPanel>
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-white mb-4">Kundendaten</h3>

            <Input
              label="Firmenname *"
              name="companyName"
              value={formData.companyName}
              onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
              required
            />

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Vorname (Ansprechpartner)"
                name="contactFirstName"
                value={formData.contactFirstName}
                onChange={(e) => setFormData({ ...formData, contactFirstName: e.target.value })}
              />
              <Input
                label="Nachname"
                name="contactLastName"
                value={formData.contactLastName}
                onChange={(e) => setFormData({ ...formData, contactLastName: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="E-Mail"
                name="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
              <Input
                label="Telefon"
                name="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>

            <Input
              label="Zahlungsziel (Tage)"
              name="defaultPaymentDays"
              type="number"
              min={0}
              max={365}
              value={formData.defaultPaymentDays}
              onChange={(e) => {
                const days = Number.parseInt(e.target.value, 10);
                setFormData({
                  ...formData,
                  defaultPaymentDays: Number.isNaN(days) ? 14 : days,
                });
              }}
            />

            <div>
              <label className="block text-sm font-medium text-white/70 mb-1.5">Notizen</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full px-4 py-2.5 rounded-lg bg-white/[0.03] border border-white/[0.08] text-white placeholder:text-white/30 focus:outline-none focus:border-white/20 focus:bg-white/[0.05] transition-colors resize-none"
                rows={3}
              />
            </div>
          </div>
        </GlassPanel>

        {/* 006b P2: die eine Save-Bar (PATTERNS.md §3) */}
        <FormSaveBar className="mt-4">
          {onCancel && (
            <Button type="button" variant="secondary" onClick={requestCancel} disabled={isLoading}>
              Abbrechen
            </Button>
          )}
          <Button type="submit" loading={isLoading}>
            {customer ? 'Speichern' : 'Erstellen'}
          </Button>
        </FormSaveBar>
      </fieldset>
      <Modal
        isOpen={confirmCancel}
        onClose={() => {
          if (!isLoading) setConfirmCancel(false);
        }}
        title="Änderungen verwerfen?"
      >
        <p className="text-white/70 mb-6">Ungespeicherte Kundendaten gehen verloren.</p>
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setConfirmCancel(false)}
            disabled={isLoading}
          >
            Weiter bearbeiten
          </Button>
          <Button type="button" variant="danger" onClick={discardAndCancel} disabled={isLoading}>
            Änderungen verwerfen
          </Button>
        </div>
      </Modal>
    </form>
  );
}

export default CustomerForm;
